/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `barometer_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class BarometerDataMesg : Mesg {
    public constructor() : super("BarometerData", Profile.MesgNum.BAROMETER_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of the timestamp */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond part of the timestamp. */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in sample_time_offset. */
    public val numSampleTimeOffset: Int
        get() = getField(SAMPLE_TIME_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** sample_time_offset, in ms. Each time in the array describes the time at which the barometer sample with the corrosponding index was taken. The samples may span across seconds. Array size must match the number of samples in baro_cal */
    public var sampleTimeOffset: List<UShort?>?
        get() {
            val field = getField(SAMPLE_TIME_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getSampleTimeOffset(it) }
        }
        set(value) {
            removeField(SAMPLE_TIME_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSampleTimeOffset(index, element) }
        }

    public fun getSampleTimeOffset(index: Int): UShort? =
        getFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, index) as? UShort

    public fun setSampleTimeOffset(index: Int, value: UShort?) {
        setFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, value, index)
    }

    /** Number of values stored in baro_pres. */
    public val numBaroPres: Int
        get() = getField(BARO_PRES_FIELD_NUM)?.numValues ?: 0

    /** baro_pres, in Pa. These are the raw ADC reading. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var baroPres: List<UInt?>?
        get() {
            val field = getField(BARO_PRES_FIELD_NUM) ?: return null
            return List(field.numValues) { getBaroPres(it) }
        }
        set(value) {
            removeField(BARO_PRES_FIELD_NUM)
            value?.forEachIndexed { index, element -> setBaroPres(index, element) }
        }

    public fun getBaroPres(index: Int): UInt? =
        getFieldValue(BARO_PRES_FIELD_NUM, index) as? UInt

    public fun setBaroPres(index: Int, value: UInt?) {
        setFieldValue(BARO_PRES_FIELD_NUM, value, index)
    }

    /** Number of values stored in enhanced_altitude. */
    public val numEnhancedAltitude: Int
        get() = getField(ENHANCED_ALTITUDE_FIELD_NUM)?.numValues ?: 0

    /** enhanced_altitude, in m. */
    public var enhancedAltitude: List<Double?>?
        get() {
            val field = getField(ENHANCED_ALTITUDE_FIELD_NUM) ?: return null
            return List(field.numValues) { getEnhancedAltitude(it) }
        }
        set(value) {
            removeField(ENHANCED_ALTITUDE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setEnhancedAltitude(index, element) }
        }

    public fun getEnhancedAltitude(index: Int): Double? =
        getFieldValue(ENHANCED_ALTITUDE_FIELD_NUM, index) as? Double

    public fun setEnhancedAltitude(index: Int, value: Double?) {
        setFieldValue(ENHANCED_ALTITUDE_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SAMPLE_TIME_OFFSET_FIELD_NUM: UByte = 1u
        public const val BARO_PRES_FIELD_NUM: UByte = 2u
        public const val ENHANCED_ALTITUDE_FIELD_NUM: UByte = 3u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SampleTimeOffset", 1u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[2] = Field("BaroPres", 2u, BaseType.UINT32, 1.0, 0.0, "Pa", false)
            fields[3] = Field("EnhancedAltitude", 3u, BaseType.UINT32, 5.0, 500.0, "m", false)
            return fields
        }
    }
}
